/**
 * Pulls every page an async option loader can return for one search + filter
 * combination.
 *
 * "Select All" used to act on the options already pulled into the menu, so on a
 * lazy-loaded picker it only ever selected the first page. Selecting everything
 * means walking the pages ourselves.
 *
 * `maxPages` is a stop so a bad `hasMore` can't loop forever. When we hit it and
 * the loader still says there is more, `truncated` comes back true and the
 * caller tells the user rather than pretending it got everything.
 */
const collectAllOptions = async ({
    loadOptions,
    search = '',
    filters = {},
    pageSize = 200,
    maxPages = 25,
    getId,
}) => {
    const options = [];
    const seen = new Set();

    for (let page = 1; page <= maxPages; page += 1) {
        const result = await loadOptions({ search, page, pageSize, ...filters });

        (Array.isArray(result?.options) ? result.options : []).forEach((option) => {
            const id = getId(option);
            if (id === undefined || seen.has(id)) return;
            seen.add(id);
            options.push(option);
        });

        // An empty page is not the end: a loader may drop rows it has already
        // selected, so a whole page can filter down to nothing while the server
        // still has more. Only hasMore ends the walk.
        if (!result?.hasMore) return { options, truncated: false };
    }

    return { options, truncated: true };
};

export { collectAllOptions };
